/**
 * Who is signed in, for the parts of the site that need to know.
 *
 * Everything that existed before fantasy betting is anonymous and stays that way — the schedule, the
 * model card, the bet record. This context is read only by the fantasy pages, so a reader who never
 * opens them never signs in and never notices any of it.
 *
 * The token lives in `localStorage` rather than a cookie: the API takes a bearer header, there is no
 * cross-site form to protect, and a value the page reads explicitly cannot be attached to a request
 * by accident. It is long-lived by design — a year — because this is a site people visit between
 * tournaments, and the server holds the revocation.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { api, setAuthToken, type Me } from './api'

const TOKEN_KEY = 'dota-oracle-token'

interface AuthState {
  me: Me | null
  loading: boolean
  signIn: (handle: string, password: string) => Promise<void>
  signUp: (handle: string, password: string) => Promise<void>
  signOut: () => void
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(TOKEN_KEY)
    } catch {
      // Private browsing, or storage disabled. Signing in still works for this page view; it just
      // does not survive a reload, which is better than the whole site failing to render.
      return null
    }
  })

  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(token !== null)

  // Every request the api module makes reads this, so the header is set in one place rather than
  // threaded through every call site.
  setAuthToken(token)

  useEffect(() => {
    if (token === null) {
      setMe(null)
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)

    api
      .me()
      .then((result) => {
        if (!cancelled) setMe(result)
      })
      .catch(() => {
        // The token was rejected — expired, or superseded by a password reset. Dropping it here is
        // what turns "every page silently 401s" into "you are signed out".
        if (cancelled) return
        setMe(null)
        setToken(null)
        try {
          window.localStorage.removeItem(TOKEN_KEY)
        } catch {
          // Nothing to do; the in-memory token is already gone.
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [token])

  const store = useCallback((next: string) => {
    try {
      window.localStorage.setItem(TOKEN_KEY, next)
    } catch {
      // See above: a session that does not survive a reload beats no session at all.
    }
    setToken(next)
  }, [])

  const signIn = useCallback(
    async (handle: string, password: string) => {
      store((await api.login(handle, password)).token)
    },
    [store],
  )

  const signUp = useCallback(
    async (handle: string, password: string) => {
      store((await api.signup(handle, password)).token)
    },
    [store],
  )

  const signOut = useCallback(() => {
    try {
      window.localStorage.removeItem(TOKEN_KEY)
    } catch {
      // Already gone as far as this page is concerned.
    }
    setToken(null)
  }, [])

  const value = useMemo<AuthState>(
    () => ({ me, loading, signIn, signUp, signOut }),
    [me, loading, signIn, signUp, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext)
  if (value === null) {
    throw new Error('useAuth must be used inside an AuthProvider.')
  }
  return value
}
